import "$styles/index.css"
import "$styles/syntax-highlighting.css"

// Import all JavaScript & CSS files from src/_components
import components from "$components/**/*.{js,jsx,js.rb,css}"

console.info("Bridgetown is loaded!")

function initMobileNav() {
  const toggle = document.querySelector("[data-mobile-nav-toggle]")
  const menu = document.querySelector("[data-mobile-nav-menu]")
  if (!toggle || !menu) return

  function setOpen(nextOpen) {
    menu.classList.toggle("hidden", !nextOpen)
    toggle.setAttribute("aria-expanded", nextOpen ? "true" : "false")
  }

  setOpen(false)

  toggle.addEventListener("click", () => {
    const isOpen = toggle.getAttribute("aria-expanded") === "true"
    setOpen(!isOpen)
  })

  window.addEventListener("resize", () => {
    if (window.innerWidth >= 1024) setOpen(false)
  })
}

function initServicesAccordion() {
  const section = document.querySelector("[data-services-section]")
  if (!section) return

  const accordion = section.querySelector("[data-services-accordion]")
  if (!accordion) return

  const image = section.querySelector("[data-services-image]")

  const items = Array.from(accordion.querySelectorAll("[data-accordion-item]"))
  if (items.length === 0) return

  function closeItem(item) {
    const trigger = item.querySelector("[data-accordion-trigger]")
    const panel = item.querySelector("[data-accordion-panel]")
    const title = item.querySelector("[data-accordion-title]")
    const icon = item.querySelector("[data-accordion-icon]")

    if (trigger) {
      trigger.classList.remove("pl-4")
      trigger.setAttribute("aria-expanded", "false")
    }
    if (title) {
      title.classList.remove("text-white")
      title.classList.add("text-white/70")
    }
    if (icon) {
      icon.classList.remove("bg-white/20", "rotate-45")
    }
    if (panel) {
      panel.classList.remove("opacity-100")
      panel.classList.add("opacity-0")
      panel.style.maxHeight = "0px"
    }
  }

  function openItem(item) {
    const trigger = item.querySelector("[data-accordion-trigger]")
    const panel = item.querySelector("[data-accordion-panel]")
    const title = item.querySelector("[data-accordion-title]")
    const icon = item.querySelector("[data-accordion-icon]")

    if (trigger) {
      trigger.classList.add("pl-4")
      trigger.setAttribute("aria-expanded", "true")
    }
    if (title) {
      title.classList.add("text-white")
      title.classList.remove("text-white/70")
    }
    if (icon) {
      icon.classList.add("bg-white/20", "rotate-45")
    }

    if (panel) {
      panel.classList.add("opacity-100")
      panel.classList.remove("opacity-0")
      panel.style.maxHeight = "0px"
      const targetHeight = panel.scrollHeight
      panel.style.maxHeight = `${targetHeight}px`
    }

    if (image && trigger) {
      const nextSrc = trigger.getAttribute("data-service-image")
      const nextAlt = trigger.getAttribute("data-service-title")
      if (nextSrc) image.setAttribute("src", nextSrc)
      if (nextAlt) image.setAttribute("alt", nextAlt)
    }
  }

  function setActiveItem(nextItem) {
    items.forEach((item) => {
      if (item === nextItem) return
      closeItem(item)
    })
    openItem(nextItem)
  }

  // Initial state
  const defaultTrigger = accordion.querySelector("[data-accordion-default-open]")
  const defaultItem = defaultTrigger?.closest("[data-accordion-item]")
  setActiveItem(defaultItem || items[0])

  // Click handlers
  items.forEach((item) => {
    const trigger = item.querySelector("[data-accordion-trigger]")
    if (!trigger) return

    trigger.addEventListener("click", () => {
      setActiveItem(item)
    })
  })

  // Keep open panels sized correctly on resize
  window.addEventListener("resize", () => {
    items.forEach((item) => {
      const panel = item.querySelector("[data-accordion-panel]")
      if (!panel) return
      if (!panel.classList.contains("opacity-100")) return
      panel.style.maxHeight = `${panel.scrollHeight}px`
    })
  })
}

function initFaqsAccordion() {
  const section = document.querySelector("[data-faqs-section]")
  if (!section) return

  const items = Array.from(section.querySelectorAll("[data-faq-item]"))
  if (items.length === 0) return

  function closeItem(item) {
    const trigger = item.querySelector("[data-faq-trigger]")
    const panel = item.querySelector("[data-faq-panel]")
    const icon = item.querySelector("[data-faq-icon]")

    if (trigger) trigger.setAttribute("aria-expanded", "false")
    if (icon) icon.classList.remove("rotate-180")
    if (panel) {
      panel.classList.remove("opacity-100")
      panel.classList.add("opacity-0")
      panel.style.maxHeight = "0px"
    }
  }

  function openItem(item) {
    const trigger = item.querySelector("[data-faq-trigger]")
    const panel = item.querySelector("[data-faq-panel]")
    const icon = item.querySelector("[data-faq-icon]")

    if (trigger) trigger.setAttribute("aria-expanded", "true")
    if (icon) icon.classList.add("rotate-180")
    if (panel) {
      panel.classList.add("opacity-100")
      panel.classList.remove("opacity-0")
      panel.style.maxHeight = "0px"
      panel.style.maxHeight = `${panel.scrollHeight}px`
    }
  }

  function toggleItem(item) {
    const trigger = item.querySelector("[data-faq-trigger]")
    const isOpen = trigger?.getAttribute("aria-expanded") === "true"
    if (isOpen) closeItem(item)
    else openItem(item)
  }

  items.forEach((item) => {
    closeItem(item)
    const trigger = item.querySelector("[data-faq-trigger]")
    if (!trigger) return
    trigger.addEventListener("click", () => toggleItem(item))
  })

  window.addEventListener("resize", () => {
    items.forEach((item) => {
      const trigger = item.querySelector("[data-faq-trigger]")
      const panel = item.querySelector("[data-faq-panel]")
      if (!trigger || !panel) return
      if (trigger.getAttribute("aria-expanded") !== "true") return
      panel.style.maxHeight = `${panel.scrollHeight}px`
    })
  })
}

function initContactForm() {
  const form = document.querySelector("[data-contact-form]")
  if (!form) return

  const statusEl = document.querySelector("[data-contact-form-status]")

  function setStatus(message, tone) {
    if (!statusEl) return
    statusEl.textContent = message
    statusEl.classList.toggle("text-green-600", tone === "success")
    statusEl.classList.toggle("text-red-600", tone === "error")
    statusEl.classList.toggle("text-gray-500", tone === "neutral")
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    setStatus("Sending...", "neutral")

    const formData = new FormData(form)
    const webhookUrl = window.__CONTACT_WEBHOOK_URL__
    const targetUrl = webhookUrl || "/api/leads"

    try {
      const useNoCors = Boolean(webhookUrl)
      const res = await fetch(targetUrl, {
        method: "POST",
        mode: useNoCors ? "no-cors" : "same-origin",
        headers: useNoCors ? undefined : { "Accept": "application/json" },
        body: formData,
      })

      if (useNoCors) {
        setStatus("Thanks! We received your request and will follow up soon.", "success")
        form.reset()
        return
      }

      const json = await res.json().catch(() => null)
      if (!res.ok || !json?.ok) {
        const msg = json?.error || "Something went wrong. Please try again."
        setStatus(msg, "error")
        return
      }

      setStatus("Thanks! We received your request and will follow up soon.", "success")
      form.reset()
    } catch (_e) {
      setStatus("Network error. Please try again.", "error")
    }
  })
}

document.addEventListener("DOMContentLoaded", () => {
  initMobileNav()
  initServicesAccordion()
  initFaqsAccordion()
  initContactForm()
})
